"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolveNames = exports.resolveAge = exports.resolveGender = exports.safeString = exports.resolveOptin = exports.findSurveyConfigModule = exports.findPreSaveSurveyConfigModule = void 0;
const fans_1 = require("../artists/fans");
const findPreSaveSurveyConfigModule = (modules = []) => {
    for (let index = 0; index < modules.length; index++) {
        const mod = modules[index];
        const found = mod.type === "PreSaveRelease"
            ? mod
            : (0, exports.findPreSaveSurveyConfigModule)(mod.modules);
        if (found) {
            return found;
        }
    }
};
exports.findPreSaveSurveyConfigModule = findPreSaveSurveyConfigModule;
const findSurveyConfigModule = (modules = [], type) => {
    for (let index = 0; index < modules.length; index++) {
        const mod = modules[index];
        const found = mod.type === type ? mod : (0, exports.findSurveyConfigModule)(mod.modules, type);
        if (found) {
            return found;
        }
    }
};
exports.findSurveyConfigModule = findSurveyConfigModule;
/**
 * Resolves the fan's opt-in status based on survey responses and configuration.
 * Calculates the `optin` map, `consent` status, and `smsOptIn` status.
 *
 * @param responses The survey responses object.
 * @param surveyVirtualOptIns The virtual opt-ins configuration from the survey.
 * @returns An object containing `optin` (map or undefined), `consent` (boolean or undefined), and `smsOptIn` (boolean or undefined).
 */
const resolveOptin = (responses, surveyVirtualOptIns) => {
    var _a;
    const optInResponse = responses.optIn;
    const optinMap = {};
    let consent = undefined;
    let smsOptIn = undefined;
    (_a = optInResponse === null || optInResponse === void 0 ? void 0 : optInResponse.value) === null || _a === void 0 ? void 0 : _a.forEach((optInValue) => {
        var _a, _b;
        // special handling for optInTermsPrivacy1 and optInText
        if (optInValue === "optInTermsPrivacy1") {
            consent = true;
            (_a = surveyVirtualOptIns.terms) === null || _a === void 0 ? void 0 : _a.forEach((vOptIn) => {
                optinMap[vOptIn] = true;
            });
        }
        if (optInValue === "optInText") {
            smsOptIn = true;
            (_b = surveyVirtualOptIns.sms) === null || _b === void 0 ? void 0 : _b.forEach((vOptIn) => {
                optinMap[vOptIn] = true;
            });
        }
        // write all optin keys to optin map in fan record
        optinMap[optInValue.replace("optin-", "")] = true;
    });
    return {
        optin: Object.keys(optinMap).length > 0 ? optinMap : undefined,
        consent,
        smsOptIn,
    };
};
exports.resolveOptin = resolveOptin;
/**
 * Safely parses a survey response value into an integer.
 * Handles number and string types.
 *
 * @param value The survey response value.
 * @returns The parsed integer, or undefined if parsing fails or the type is invalid.
 */
const safeParseInt = (value) => typeof value === "number"
    ? value
    : typeof value === "string"
        ? parseInt(value)
        : undefined;
/**
 * Safely converts a survey response value to a string.
 * If the value is a string, it's returned directly.
 * If it's an array, its elements are joined using the provided separator.
 * If it's a number, it's converted to its string representation.
 * If the value is an array, each element is processed with safeString before joining.
 *
 * @param value The survey response value.
 * @param join The separator string to use when joining array elements (default: " ").
 * @returns The string representation, or undefined if the type is invalid.
 */
const safeString = (value, join = " ") => typeof value === "string"
    ? value
    : typeof value === "number"
        ? String(value) // Convert number to string
        : Array.isArray(value)
            ? value
                .map((item) => (0, exports.safeString)(item, join)) // Recursively apply safeString to each item
                .filter((item) => item !== undefined) // Filter out undefined results
                .join(join) // Join the processed strings
            : undefined;
exports.safeString = safeString;
const isValidGenderKey = (key) => typeof key === "string" && key in fans_1.Gender;
/**
 * Safely extracts the fan's gender from survey responses.
 * It checks `responses.gender.value` and `responses['profiling.gender'].value`.
 * It handles array values by taking the first element.
 * It validates the result against the `Gender` enum.
 *
 * @param responses The survey responses object.
 * @returns The validated gender as a `Gender` enum member, or undefined if no valid gender could be determined.
 */
const resolveGender = (responses) => {
    var _a;
    const value = (_a = (responses.gender || responses["profiling.gender"])) === null || _a === void 0 ? void 0 : _a.value;
    const genderValue = Array.isArray(value) ? value[0] : value;
    return isValidGenderKey(genderValue) ? genderValue : undefined;
};
exports.resolveGender = resolveGender;
/**
 * Safely extracts or calculates the fan's age from survey responses.
 * It checks the following fields in order:
 * 1. `responses.age.value`
 * 2. `responses['profiling.age'].value`
 * 3. Calculates from `responses['profiling.yearborn'].value`
 *
 * @param responses The survey responses object.
 * @returns The extracted or calculated age as a number, or undefined if no valid age could be determined.
 */
const resolveAge = (responses) => {
    var _a, _b, _c;
    // Check responses.age
    let ageValue = (_a = responses.age) === null || _a === void 0 ? void 0 : _a.value;
    let parsedAge = safeParseInt(ageValue);
    if (parsedAge !== undefined) {
        return parsedAge;
    }
    // Check responses["profiling.age"]
    ageValue = (_b = responses["profiling.age"]) === null || _b === void 0 ? void 0 : _b.value;
    parsedAge = safeParseInt(ageValue);
    if (parsedAge !== undefined) {
        return parsedAge;
    }
    // Check responses["profiling.yearborn"]
    const year = safeParseInt((_c = responses["profiling.yearborn"]) === null || _c === void 0 ? void 0 : _c.value);
    if (year !== undefined) {
        const currentYear = new Date().getFullYear();
        // Basic validation for the year
        if (year > 1900 && year <= currentYear) {
            return currentYear - year;
        }
        // Invalid year falls through and returns undefined
    }
    // Return undefined if no valid age source is found
    return undefined;
};
exports.resolveAge = resolveAge;
/**
 * Resolves the first and last name from the survey responses.
 * It checks `responses.name.value`.
 * If the value is a string, it attempts to split it into first and last name based on whitespace (\s+).
 * For multi-part names (e.g., 'Joe Bob Law'), the last part becomes the last name ('Law')
 * and the preceding parts become the first name ('Joe Bob').
 * If the value is an array, it assumes the first element is the first name and the second is the last name.
 *
 * @param responses The survey responses object.
 * @returns An object containing `firstName` and `lastName` (both potentially undefined).
 */
const resolveNames = (responses) => {
    var _a;
    const nameValue = (_a = responses.name) === null || _a === void 0 ? void 0 : _a.value;
    let firstName = undefined;
    let lastName = undefined;
    if (typeof nameValue === "string") {
        // Trim whitespace and split by one or more spaces
        const parts = nameValue.trim().split(/\s+/);
        if (parts.length > 0) {
            if (parts.length === 1) {
                firstName = parts[0] || undefined; // Handle potential empty string after trim
            }
            else {
                lastName = parts.pop(); // Get the last part as last name
                firstName = parts.join(" ") || undefined; // Join the remaining parts for first name
            }
        }
    }
    else if (Array.isArray(nameValue)) {
        // Assuming array format [firstName, lastName], ensure they are strings
        firstName = typeof nameValue[0] === "string" ? nameValue[0] : undefined;
        lastName = typeof nameValue[1] === "string" ? nameValue[1] : undefined;
    }
    return { firstName, lastName };
};
exports.resolveNames = resolveNames;
//# sourceMappingURL=utils.js.map